/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/solidjs-css
 * @description Renders the vanilla CSS adapter status badge for the SolidJS
 * CSS variant. The badge exposes the shared adapter status vocabulary in a
 * compact, themeable visual treatment.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { cx } from "../lib/cx";

const LABELS: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

/**
 * Props for rendering the SolidJS CSS adapter status badge.
 *
 * @property status Shared adapter status vocabulary value to display.
 * @property showLabel Whether the human-readable label should be rendered.
 * @property class Optional class name merged into the badge wrapper.
 */
export interface AdapterStatusBadgeProps {
  status: AdapterStatusType;
  showLabel?: boolean;
  class?: string;
}

/**
 * Renders a compact SolidJS CSS badge for the current adapter lifecycle state.
 *
 * @param props Badge props including the status token and optional label visibility.
 * @returns A styled badge element that mirrors the shared adapter status taxonomy.
 */
export function AdapterStatusBadge(
  props: AdapterStatusBadgeProps,
): JSX.Element {
  if (props.showLabel === false) {
    return (
      <span
        class={cx(
          "gn-adapters-badge",
          `gn-adapters-badge--${props.status}`,
          props.class,
        )}
        aria-label={LABELS[props.status]}
        role="img"
      >
        <span aria-hidden="true" class="gn-adapters-badge__dot" />
      </span>
    );
  }

  return (
    <span
      class={cx(
        "gn-adapters-badge",
        `gn-adapters-badge--${props.status}`,
        props.class,
      )}
    >
      <span class="gn-adapters-badge__dot" />
      <span>{LABELS[props.status]}</span>
    </span>
  );
}
